import React, { useState, useEffect } from 'react';
import { Upload, CheckCircle } from 'lucide-react';
import { commissionTypes } from '../data/mock';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Commission = () => {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refFiles, setRefFiles] = useState([]);
  const [formData, setFormData] = useState({
    clientName: '',
    contactMethod: '',
    paypal: '',
    commissionType: '',
    unityUpload: false,
    nsfwWork: false,
    description: '',
  });

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.commissionType || !formData.description) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      // Upload reference images first (if any)
      let referenceImageIds = [];
      if (refFiles.length > 0) {
        const uploads = await Promise.all(
          refFiles.map((f) => {
            const fd = new FormData();
            fd.append('file', f);
            return axios.post(`${API}/commissions/upload`, fd);
          })
        );
        referenceImageIds = uploads.map((r) => r.data.id);
      }

      await axios.post(`${API}/commissions`, {
        client_name: formData.clientName,
        contact_method: formData.contactMethod,
        paypal: formData.paypal,
        commission_type: formData.commissionType,
        unity_upload: formData.unityUpload,
        nsfw_work: formData.nsfwWork,
        description: formData.description,
        reference_image_ids: referenceImageIds,
      });
      setSubmitted(true);
      toast({
        title: 'Commission Submitted!',
        description: 'Your request has been added to the queue. I\'ll get back to you soon!',
      });
      setFormData({
        clientName: '',
        contactMethod: '',
        paypal: '',
        commissionType: '',
        unityUpload: false,
        nsfwWork: false,
        description: '',
      });
      setRefFiles([]);
    } catch (err) {
      console.error('Failed to submit commission:', err);
      toast({
        title: 'Submission Failed',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="transition-all duration-700 opacity-100">
            <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-white mb-4">Commission Submitted!</h1>
            <p className="text-xl text-gray-300 mb-8">
              Your request has been added to the queue. I'll reach out to you soon!
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-md px-8 py-3 bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 hover:from-teal-500 hover:via-cyan-500 hover:to-teal-500 text-white shadow-lg shadow-teal-500/50 hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              Submit Another
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 px-4">
      <div className="max-w-3xl mx-auto">
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-300 via-teal-400 to-cyan-400 bg-clip-text text-transparent mb-4">
            Request a Commission
          </h1>
          <p className="text-xl text-gray-300">
            Fill out the form below and I'll get back to you as soon as possible
          </p>
        </div>

        <div
          className={`transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="rounded-xl border bg-gradient-to-br from-stone-800/50 via-neutral-800/40 to-slate-800/50 border-teal-500/40 backdrop-blur-xl shadow-2xl shadow-teal-500/30">
            <div className="p-6">
              <h2 className="font-semibold tracking-tight text-2xl text-white">
                Commission Request Form
              </h2>
            </div>
            <div className="p-6 pt-0">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      Name <span className="text-gray-500 font-normal">(optional)</span>
                    </label>
                    <input
                      data-testid="commission-name-input"
                      className="flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      placeholder="Your name or nickname"
                      value={formData.clientName}
                      onChange={(e) =>
                        setFormData({ ...formData, clientName: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      Contact Method
                    </label>
                    <input
                      data-testid="commission-contact-input"
                      className="flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      placeholder="Discord username, phone, etc."
                      value={formData.contactMethod}
                      onChange={(e) =>
                        setFormData({ ...formData, contactMethod: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      PayPal Email
                    </label>
                    <input
                      type="email"
                      className="flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      placeholder="For payment processing"
                      value={formData.paypal}
                      onChange={(e) =>
                        setFormData({ ...formData, paypal: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      Commission Type *
                    </label>
                    <Select
                      value={formData.commissionType}
                      onValueChange={(val) =>
                        setFormData({ ...formData, commissionType: val })
                      }
                    >
                      <SelectTrigger className="w-full bg-slate-800/50 border-slate-600 text-white">
                        <SelectValue placeholder="Select commission type" />
                      </SelectTrigger>
                      <SelectContent>
                        {commissionTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="unity_upload"
                      checked={formData.unityUpload}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, unityUpload: checked })
                      }
                    />
                    <label className="text-sm font-medium text-white" htmlFor="unity_upload">
                      Add Unity upload service (+$10-15)
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="nsfw_work"
                      checked={formData.nsfwWork}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, nsfwWork: checked })
                      }
                    />
                    <label className="text-sm font-medium text-white" htmlFor="nsfw_work">
                      Add NSFW work (+$5-10)
                    </label>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-white block mb-1.5">
                      Commission Details *
                    </label>
                    <textarea
                      className="flex min-h-[60px] w-full rounded-md border px-3 py-2 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      placeholder="Describe what you want done, colors, style preferences, any specific details..."
                      required
                      rows={6}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-white mb-2 block">
                    Reference Images <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <input
                    data-testid="commission-reference-input"
                    type="file"
                    className="flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm bg-slate-800/50 border-slate-600 text-white file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-gray-300"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    onChange={(e) => setRefFiles(Array.from(e.target.files))}
                  />
                  {refFiles.length > 0 && (
                    <p className="text-xs text-cyan-400 mt-1.5" data-testid="commission-reference-count">
                      {refFiles.length} image{refFiles.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                <button
                  className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-md px-8 w-full py-3 bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 hover:from-teal-500 hover:via-cyan-500 hover:to-teal-500 text-white shadow-lg shadow-teal-500/50 hover:shadow-xl hover:shadow-teal-500/70 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={submitting}
                >
                  <Upload className="w-5 h-5" />
                  {submitting ? 'Submitting...' : 'Submit Commission Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Commission;
